import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, ModalHandle, SoftwareUpdateAlert, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { RdpLink } from '../Core/RdpLink';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayEditModal } from './DisplayView/Settings/DisplayEditModal';
import { DisplayEventsModal } from './DisplayEventsModal';
import { TerminalModal } from './TerminalModal';
import { TicketModal } from './TicketModal';
import { TicketsModal } from './TicketsModal';
import { BsCheck } from '@react-icons/all-files/bs/BsCheck';
import { BsCircle } from '@react-icons/all-files/bs/BsCircle';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';
import { BsFillLightningFill } from '@react-icons/all-files/bs/BsFillLightningFill';
import { BsFillXCircleFill } from '@react-icons/all-files/bs/BsFillXCircleFill';

export interface IDisplayCardProps {
    readonly display: ActiveDisplay;
    readonly history: any;
    readonly vm: DisplayListViewModel;
}

@autoinject
@observer
export class DisplayCardSmall extends React.Component<IDisplayCardProps> {
    public render() {
        const { display } = this.props;
        return (
            <div className="col-6 col-md-4 col-xl-3 my-1">
                <ButtonGroup size="sm">
                    <button className={this.iconButtonClassName}>{this.icon}</button>
                    <LinkContainer to={`/displays/${display.serialNumber}`}>
                        <a className="btn btn-primary btn-sm">{display.serialNumber}</a>
                    </LinkContainer>
                    <UncontrolledButtonDropdown>
                        <DropdownToggle caret color="primary" size="sm" />
                        <DropdownMenu>
                            <DropdownItem
                                tag="a"
                                href={`${this.accountStore!.portalUrl}/proxy/${display.serialNumber}`}
                                target="_blank"
                            >
                                StatusVu&reg;
                            </DropdownItem>

                            <DropdownItem onClick={this.openCreateTicketModal}>Create Ticket</DropdownItem>

                            <DropdownItem onClick={this.openDisplayEditModal}>Edit</DropdownItem>

                            <DropdownItem tag={RdpLink} type="input1" display={display}>
                                VNC Player 1
                            </DropdownItem>
                            <DropdownItem tag={RdpLink} type="input2" display={display}>
                                VNC Player 2
                            </DropdownItem>
                            <DropdownItem tag={RdpLink} type="camera" display={display}>
                                Camera IP
                            </DropdownItem>

                            <DropdownItem onClick={this.openTerminalModal}>Terminal</DropdownItem>
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                </ButtonGroup>

                <SoftwareUpdateAlert className="ps-2" updateStatus={display.displaySoftwareUpdateStatus} />

                {this.renderModals && (
                    <span>
                        <DisplayEventsModal
                            display={display}
                            modalHandle={this.displayEventsModalHandle}
                            history={this.props.history}
                        />
                        <DisplayEditModal display={display} modalHandle={this.displayEditModalHandle} />
                        <TicketModal display={display} modalHandle={this.createTicketModalHandle} />
                        <TicketsModal
                            display={display}
                            modalHandle={this.createTicketsModalHandle}
                            history={this.props.history}
                        />
                        <TerminalModal modalHandle={this.terminalModalHandle} serialNumber={display.serialNumber} />
                    </span>
                )}
            </div>
        );
    }

    @computed
    private get icon() {
        if (this.props.display.muted) return <BsCircle />;

        switch (this.props.display.state) {
            case 'good':
                return <BsCheck />;
            case 'offline':
                return <BsCircle />;
            case 'exception':
                return <BsFillLightningFill />;
            case 'warning':
                return <BsExclamationTriangleFill />;
            case 'critical':
                return <BsFillXCircleFill />;
        }
    }

    @computed
    private get iconButtonClassName() {
        if (this.props.display.muted) return 'btn btn-gray';

        switch (this.props.display.state) {
            case 'good':
                return 'btn btn-success';
            case 'offline':
                return 'btn btn-gray';
            case 'exception':
                return 'btn btn-info';
            case 'warning':
                return 'btn btn-warning';
            case 'critical':
                return 'btn btn-danger btn-danger-group';
        }
    }

    @bind
    @action
    private openDisplayEditModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.displayEditModalHandle.open();
    }

    @bind
    @action
    private openCreateTicketModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.createTicketModalHandle.open();
    }

    @bind
    @action
    private openTerminalModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.terminalModalHandle.open();
    }

    @observable
    private renderModals = false;
    private displayEditModalHandle = new ModalHandle();
    private displayEventsModalHandle = new ModalHandle();
    private createTicketModalHandle = new ModalHandle();
    private createTicketsModalHandle = new ModalHandle();
    private terminalModalHandle = new ModalHandle();
    @inject
    private accountStore!: AccountStore;
}
